import { fetchWithTenant } from '../utils/apiClient';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4010/api';

// Create a new portfolio project
export async function createPortfolio(portfolioData) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/portfolio`, {
      method: 'POST',
      // Tenant headers added by fetchWithTenant
      credentials: 'include',
      body: JSON.stringify(portfolioData)
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to create portfolio project');
    }

    return data;
  } catch (error) {
    console.error('Error creating portfolio project:', error);
    throw error;
  }
}

// Get user's portfolio projects
export async function getUserPortfolio(params = {}) {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.status) queryParams.append('status', params.status);
    if (params.category) queryParams.append('category', params.category);
    if (params.isPublic !== undefined) queryParams.append('isPublic', params.isPublic);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const response = await fetchWithTenant(`${API_BASE_URL}/portfolio?${queryParams}`, {
      method: 'GET',
      // Tenant headers added by fetchWithTenant
      credentials: 'include'
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch portfolio projects');
    }

    return data;
  } catch (error) {
    console.error('Error fetching portfolio projects:', error);
    throw error;
  }
}

// Get all portfolio projects (Admin)
export async function getAllPortfolio(params = {}) {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.status) queryParams.append('status', params.status);
    if (params.category) queryParams.append('category', params.category);
    if (params.isPublic !== undefined) queryParams.append('isPublic', params.isPublic);
    if (params.userId) queryParams.append('userId', params.userId);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const response = await fetchWithTenant(`${API_BASE_URL}/portfolio/all?${queryParams}`, {
      method: 'GET',
      // Tenant headers added by fetchWithTenant
      credentials: 'include'
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch all portfolio projects');
    }

    return data;
  } catch (error) {
    console.error('Error fetching all portfolio projects:', error);
    throw error;
  }
}

// Get portfolio project by ID
export async function getPortfolioById(portfolioId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/portfolio/${portfolioId}`, {
      method: 'GET',
      // Tenant headers added by fetchWithTenant
      credentials: 'include'
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch portfolio project');
    }

    return data;
  } catch (error) {
    console.error('Error fetching portfolio project:', error);
    throw error;
  }
}

// Update portfolio project
export async function updatePortfolio(portfolioId, updateData) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/portfolio/${portfolioId}`, {
      method: 'PUT',
      // Tenant headers added by fetchWithTenant
      credentials: 'include',
      body: JSON.stringify(updateData)
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update portfolio project');
    }

    return data;
  } catch (error) {
    console.error('Error updating portfolio project:', error);
    throw error;
  }
}

// Delete portfolio project
export async function deletePortfolio(portfolioId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/portfolio/${portfolioId}`, {
      method: 'DELETE',
      // Tenant headers added by fetchWithTenant
      credentials: 'include'
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(`${data.error || 'Failed to delete portfolio project'} ${data.message || ''}`);
    }

    return data;
  } catch (error) {
    console.error('Error deleting portfolio project:', error.message);
    throw error;
  }
}

// Archive portfolio project
export async function archivePortfolio(portfolioId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/portfolio/${portfolioId}/archive`, {
      method: 'PATCH',
      // Tenant headers added by fetchWithTenant
      credentials: 'include'
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to archive portfolio project');
    }

    return data;
  } catch (error) {
    console.error('Error archiving portfolio project:', error);
    throw error;
  }
}

// Activate portfolio project
export async function activatePortfolio(portfolioId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/portfolio/${portfolioId}/activate`, {
      method: 'PATCH',
      // Tenant headers added by fetchWithTenant
      credentials: 'include'
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to activate portfolio project');
    }

    return data;
  } catch (error) {
    console.error('Error activating portfolio project:', error);
    throw error;
  }
}

// Toggle portfolio project public status
export async function togglePortfolioPublic(portfolioId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/portfolio/${portfolioId}/toggle-public`, {
      method: 'PATCH',
      // Tenant headers added by fetchWithTenant
      credentials: 'include'
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to toggle portfolio public status');
    }

    return data;
  } catch (error) {
    console.error('Error toggling portfolio public status:', error);
    throw error;
  }
}

// Get public portfolio projects
export async function getPublicPortfolio(params = {}) {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.category) queryParams.append('category', params.category);
    if (params.technologies) queryParams.append('technologies', params.technologies);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const response = await fetchWithTenant(`${API_BASE_URL}/portfolio/public?${queryParams}`, {
      method: 'GET',
      // Tenant headers added by fetchWithTenant
      credentials: 'include'
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch public portfolio projects');
    }

    return data;
  } catch (error) {
    console.error('Error fetching public portfolio projects:', error);
    throw error;
  }
}

// Portfolio categories
export const PORTFOLIO_CATEGORIES = {
  web_development: 'Web Development',
  mobile_development: 'Mobile Development',
  desktop_application: 'Desktop Application',
  data_science: 'Data Science',
  machine_learning: 'Machine Learning',
  blockchain: 'Blockchain',
  devops: 'DevOps',
  ui_ux_design: 'UI/UX Design',
  other: 'Other'
};

// Portfolio status options
export const PORTFOLIO_STATUS = {
  active: 'Active',
  archived: 'Archived',
  draft: 'Draft'
};
